import { env } from '@/lib/env';

import { ApiError } from './errors';

export type TokenProvider = () => string | null;
export type EnvHeaderProvider = () => string;

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
    headers?: Record<string, string>;
}

interface ApiClientConfig {
    getToken: TokenProvider;
    getEnvHeader?: EnvHeaderProvider;
}

const CONSOLE_ENV_HEADER = 'X-Console-Env';
const IDEMPOTENCY_HEADER = 'Idempotency-Key';
const WRITE_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

/**
 * Thin fetch wrapper around the SafarAPI backend. Auth via Keycloak JWT.
 * Sends X-Console-Env: TEST|LIVE so the backend resolves the active environment
 * for the current request (driven by the EnvSwitcher in the topbar).
 */
export class ApiClient {
    private readonly getToken: TokenProvider;
    private readonly getEnvHeader?: EnvHeaderProvider;

    constructor(config: ApiClientConfig) {
        this.getToken = config.getToken;
        this.getEnvHeader = config.getEnvHeader;
    }

    get<T>(path: string, signal?: AbortSignal): Promise<T> {
        return this.request<T>(path, { method: 'GET', signal });
    }

    post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
        return this.request<T>(path, { method: 'POST', body, signal });
    }

    patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
        return this.request<T>(path, { method: 'PATCH', body, signal });
    }

    delete<T>(path: string, signal?: AbortSignal): Promise<T> {
        return this.request<T>(path, { method: 'DELETE', signal });
    }

    async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
        const token = this.getToken();
        const envHeader = this.getEnvHeader?.();
        const headers: Record<string, string> = {
            Accept: 'application/json',
            ...(options.headers ?? {}),
        };
        if (token) headers.Authorization = `Bearer ${token}`;
        if (envHeader) headers[CONSOLE_ENV_HEADER] = envHeader;

        const method = options.method ?? 'GET';
        if (WRITE_METHODS.has(method) && !headers[IDEMPOTENCY_HEADER]) {
            headers[IDEMPOTENCY_HEADER] = crypto.randomUUID();
        }

        let body: BodyInit | undefined;
        if (options.body !== undefined) {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(options.body);
        }

        const url = `${env.apiBaseUrl}${path}`;
        const response = await fetch(url, {
            method,
            headers,
            body,
            signal: options.signal,
        });

        if (response.status === 204) {
            return undefined as T;
        }
        if (!response.ok) {
            throw await ApiError.fromResponse(response);
        }
        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
            return undefined as T;
        }
        return (await response.json()) as T;
    }
}
